import _ from 'lodash'
import { isNumber, uniqueBy } from 'remeda'
import {
  BillingType,
  ContractListSubmitViaFilter,
  ImportIntegrationChangeOrdersMethod,
  ImportIntegrationComboJobMethod,
  IntegrationType,
  RetentionTrackingLevel,
  TaxCalculationType,
  WriteSyncOperationStatus,
} from '../enums.js'
import type {
  CompanyIntegrationMetadata,
  CompanyIntegrationMetadataSageIntacct,
  CompanyIntegrationMetadataVista,
  CompanyIntegrationMetadataVistaPeyote,
} from '../types/company-integration.js'
import type * as integrationTypes from '../types/integration.js'
import {
  MAX_CMIC_INVOICE_NUMBER_LENGTH,
  MAX_FOUNDATION_INVOICE_NUMBER_LENGTH,
  MAX_SAGE_INVOICE_CODE_LENGTH,
} from './integration-sync.js'
import { normalizeString } from './strings.js'

const DIGITS_REGEX = /^[0-9]*$/

// Vault ID for "Siteline Customer Login" in 1Password.
// This is the only vault that our service account can access.
export const onePasswordIntegrationCredentialsVaultId = '2mivwtq423jypegq5tqsvpjsme'

export function isOnPremConnectorOfflineError(error: string): boolean {
  return (
    // Agave
    error.toLowerCase().includes('agave connector is offline') ||
    // Peyote
    error.toLowerCase().includes('on-prem connector is offline')
  )
}

// How many months of AP invoices to include in a bulk vendor invoice import
export const BULK_VENDOR_INVOICE_IMPORT_PERIOD_MONTHS = 6

// When `usesOldestCommitmentDateForBulkApImport` is enabled, we pad the oldest commitment date by
// this many months so invoices created shortly before a commitment was opened still get pulled.
export const BULK_VENDOR_INVOICE_IMPORT_COMMITMENT_BUFFER_MONTHS = 3

export type LineItemIdentifier = {
  sortOrder?: number
  code: string
  name: string
  totalValue: number
}

type Score = 'zero' | 'low' | 'medium' | 'high'

type LineItemScoring = {
  code: Score
  name: Score
  totalValue: Score
}

// Whether each integration type *can* authenticate via OAuth. The `true` keys define
// `OauthIntegrationType`; `satisfies Record<IntegrationType, boolean>` keeps it exhaustive, so a new
// integration type won't compile until it's classified here. CMiC is the one dual-mode type — it
// authenticates with either Basic Auth or OAuth depending on the customer's instance — so a `true`
// here means "may store OAuth tokens", not "always does". Callers that need the actual mode must
// check the stored credentials (`cmicPeyoteUsesOauth`).
const IS_OAUTH_INTEGRATION = {
  [IntegrationType.ACUMATICA]: false,
  [IntegrationType.ACUMATICA_PEYOTE]: true,
  [IntegrationType.CMIC]: false,
  [IntegrationType.CMIC_PEYOTE]: true,
  [IntegrationType.COMPUTER_EASE_FILE]: false,
  [IntegrationType.FOUNDATION]: false,
  [IntegrationType.FOUNDATION_FILE]: false,
  [IntegrationType.FOUNDATION_PEYOTE]: false,
  [IntegrationType.GC_PAY]: false,
  [IntegrationType.NETSUITE_FILE]: false,
  [IntegrationType.PROCORE]: false,
  [IntegrationType.PROCORE_PEYOTE]: true,
  [IntegrationType.QUICKBOOKS_ENTERPRISE_FILE]: false,
  [IntegrationType.QUICKBOOKS_ONLINE]: false,
  [IntegrationType.QUICKBOOKS_ONLINE_PEYOTE]: true,
  [IntegrationType.SAGE_100_CONTRACTOR]: false,
  [IntegrationType.SAGE_100_CONTRACTOR_AGAVE]: false,
  [IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]: false,
  [IntegrationType.SAGE_300_CRE]: false,
  [IntegrationType.SAGE_300_CRE_AGAVE]: false,
  [IntegrationType.SAGE_INTACCT]: false,
  [IntegrationType.SAGE_INTACCT_PEYOTE]: false,
  [IntegrationType.SPECTRUM]: false,
  [IntegrationType.SPECTRUM_PEYOTE]: false,
  [IntegrationType.TEST]: false,
  [IntegrationType.TEXTURA]: false,
  [IntegrationType.VISTA]: false,
  [IntegrationType.VISTA_PEYOTE]: false,
} as const satisfies Record<IntegrationType, boolean>

/** The integration types that can authenticate via OAuth (Procore/QBO/Acumatica/CMiC peyote). */
export type OauthIntegrationType = {
  [K in IntegrationType]: (typeof IS_OAUTH_INTEGRATION)[K] extends true ? K : never
}[IntegrationType]

/** Whether an integration can authenticate via OAuth (and thus store a refresh-token credential). */
export function isOauthIntegration(type: IntegrationType): type is OauthIntegrationType {
  return IS_OAUTH_INTEGRATION[type]
}

export function getScoringForIntegration(type: IntegrationType): LineItemScoring {
  switch (type) {
    // Acumatica has a code that either matches the line item position, or is
    // always empty. It is irrelevant to matching and could potentially mess up the algorithm if
    // items were reordered.
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
      return { code: 'zero', name: 'medium', totalValue: 'low' }

    // When reading line items from Procore, we infer the code from the line number and change order position.
    // When writing back to Procore, we can extract the line number from the invoice.
    // Because of the way we infer the code, it is not very reliable, so we give it a low score.
    // Approved SSOVs cannot be changed, we're not concerned about line numbers changing over time.
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
      return { code: 'low', name: 'high', totalValue: 'medium' }

    // Use custom scoring when matching line items in Sage. The "code" isn't as useful because the ERP
    // enforces a 2 alphanumeric character max on codes, so it's common for folks to use 1, 2, 3, etc.
    // and we have many false positives in Siteline.
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return { code: 'low', name: 'high', totalValue: 'medium' }

    // For other integrations, code is usually the most significant because it has less variance
    // in case and special characters. Name can change a lot, and the total value can have a lot of duplicates.
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return { code: 'high', name: 'medium', totalValue: 'low' }
  }
}

export type Match<Source, Destination> = {
  source: Source
  destination: Destination
  score: number
}

type DuplicateSource<Source, Destination> = {
  sources: Source[]
  destination: Destination
}

type DuplicateDestination<Source, Destination> = {
  source: Source
  destinations: Destination[]
}

export type MatchResult<Source, Destination> = {
  matches: Match<Source, Destination>[]
  duplicateSources: DuplicateSource<Source, Destination>[]
  duplicateDestinations: DuplicateDestination<Source, Destination>[]
  extraSources: Source[]
  extraDestinations: Destination[]
  emptySources: Source[]
  emptyDestinations: Destination[]
}

export function getLineItemDescriptionForMatching(lineItem: LineItemIdentifier): string {
  return `code '${lineItem.code}' (name '${lineItem.name}')`
}

/**
 * Returns the name of an integration, with an optional `shortName` parameter.
 * In most cases:
 *  - For displaying an integration name within the context of a contract, use `shortName = true`
 *  - For displaying an integration name outside of a contract, use `shortName = false` (the default)
 */
export function getIntegrationBaseName(type: IntegrationType, shortName = false): string {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
      return 'Acumatica'
    case IntegrationType.GC_PAY:
      return 'GCPay'
    case IntegrationType.TEXTURA:
      return 'Textura'
    case IntegrationType.TEST:
      return 'Test'
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
      return 'Procore'
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
      return 'Foundation'
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      return shortName ? 'Sage' : 'Sage 100'
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return shortName ? 'Sage' : 'Sage 300'
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return shortName ? 'Sage' : 'Sage Intacct'
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
      return 'Spectrum'
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return 'Vista'
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
      return 'QuickBooks'
    case IntegrationType.FOUNDATION_FILE:
      return shortName ? 'Foundation' : 'Foundation (file)'
    case IntegrationType.COMPUTER_EASE_FILE:
      return 'ComputerEase'
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return 'CMiC'
    case IntegrationType.NETSUITE_FILE:
      return 'NetSuite'
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return 'QuickBooks Online'
  }
}

/**
 * Returns the long/short name of an integration by type and optional label.
 * In most cases, you can just use `integration.shortName` / `integration.longName` returned by the GraphQL API.
 */
export function getIntegrationName(
  type: IntegrationType,
  shortName = false,
  label?: string | null
): string {
  const base = getIntegrationBaseName(type, shortName)
  if (label && !shortName) {
    return `${base} (${label})`
  }
  return base
}

/**
 * This is the default scoring mechanism for each "significance".
 * We want 2 lows > 1 medium, 2 medium > 1 high, 3 lows > 1 high.
 *
 * "This feels right" – Oliver Manheim, Dec 2023.
 */
function scoreToPoints(score: Score): number {
  switch (score) {
    case 'zero':
      return 0
    case 'low':
      return 0.3
    case 'medium':
      return 0.5
    case 'high':
      return 0.7
  }
}

/**
 * Gets the matching score between a source and destination.
 */
function getLineItemScore(
  source: LineItemIdentifier,
  destination: LineItemIdentifier,
  scoring: LineItemScoring
): number {
  const srcCode = normalizeString(source.code)
  const dstCode = normalizeString(destination.code)
  const srcName = normalizeString(source.name)
  const dstName = normalizeString(destination.name)

  // If the item code matches its sort order, its significance should be greatly reduced to allow for
  // line item insertions.
  const srcOrderMatchesCode =
    isNumber(source.sortOrder) &&
    normalizeString(source.sortOrder.toString()) === normalizeString(srcCode)
  const dstOrderMatchesCode =
    isNumber(destination.sortOrder) &&
    normalizeString(destination.sortOrder.toString()) === normalizeString(dstCode)

  let score = 0

  // If codes match, add to the score
  if (srcCode === dstCode && srcCode.length > 0 && dstCode.length > 0) {
    if (srcOrderMatchesCode && dstOrderMatchesCode) {
      score += scoreToPoints('low')
    } else {
      score += scoreToPoints(scoring.code)
    }
  }

  // If names match, add to the score
  if (srcName === dstName && srcName.length > 0 && dstName.length > 0) {
    score += scoreToPoints(scoring.name)
  }

  // If values match, add to the score
  if (source.totalValue === destination.totalValue) {
    score += scoreToPoints(scoring.totalValue)
  }

  return score
}

/**
 * Gets the group matching score between a source and destination.
 */
function getLineItemGroupScore(
  source: LineItemGroupIdentifier,
  destination: LineItemGroupIdentifier
): number {
  const srcName = normalizeString(source.name)
  const dstName = normalizeString(destination.name)

  const srcCode = normalizeString(source.code ?? '')
  const dstCode = normalizeString(destination.code ?? '')

  let score = 0

  // If names match, add to the score
  if (srcName === dstName && srcName.length > 0 && dstName.length > 0) {
    score += 0.5
  }

  // If codes match, add to the score
  if (srcCode === dstCode && srcCode.length > 0 && dstCode.length > 0) {
    score += 1
  }

  return score
}

type GetMatchesParams<Source, Destination> = {
  sources: Source[]
  destinations: Destination[]
  getScore: (source: Source, destination: Destination) => number
  isEmpty: (item: Source | Destination) => boolean
}

/**
 * Computes the best matches given an array of sources and an array of destinations.
 * This algorithm does not throw. You must handle errors in the caller (eg: if you want to prevent extra sources).
 *
 * The algorithm is described in depth here:
 * https://www.notion.so/siteline/Integration-Line-Item-Matching-d242626db6a14d4e82dba8449d7a1f40
 *
 * Note that we use generics so that each integration can attach more data to the source/destination,
 * like a puppeteer element handle (GCPay) or an invoice data row (Textura).
 */
function getMatches<Source, Destination>({
  sources,
  destinations,
  getScore,
  isEmpty,
}: GetMatchesParams<Source, Destination>): MatchResult<Source, Destination> {
  const matches: Match<Source, Destination>[] = []
  const duplicateSources: DuplicateSource<Source, Destination>[] = []
  let duplicateDestinations: DuplicateDestination<Source, Destination>[] = []
  const extraDestinations: Destination[] = []

  const emptySources = sources.filter(isEmpty)
  const emptyDestinations = destinations.filter(isEmpty)

  // Filter out empty sources and destinations for the rest of the algorithm
  const filteredSources = _.without(sources, ...emptySources)
  const filteredDestinations = _.without(destinations, ...emptyDestinations)

  // For each line item in Destination, find sources with the highest non-0 score
  for (const dst of filteredDestinations) {
    const maxSourceScore = Math.max(...filteredSources.map((src) => getScore(src, dst)))

    // Find sources with the highest non-0 score.
    // Filter out sources that have a higher destination score.
    const potentialSources = filteredSources
      .filter((src) => getScore(src, dst) === maxSourceScore)
      .filter(
        (src) => !filteredDestinations.some((otherDst) => getScore(src, otherDst) > maxSourceScore)
      )

    // If there is no source, it's an extra destination
    if (maxSourceScore === 0 || potentialSources.length === 0) {
      extraDestinations.push(dst)
      continue
    }

    // If there are multiple sources, they're duplicate sources
    if (potentialSources.length > 1) {
      duplicateSources.push({
        sources: potentialSources,
        destination: dst,
      })
      continue
    }

    const src = potentialSources[0]

    // If another destination has the same score for the same source, it's a duplicate destination
    const destinationsWithSameScore = filteredDestinations.filter(
      (candidateDst) => getScore(src, candidateDst) === maxSourceScore
    )
    if (destinationsWithSameScore.length > 1) {
      duplicateDestinations.push({
        source: src,
        destinations: destinationsWithSameScore,
      })
      continue
    }

    // Otherwise, assign source => destination
    matches.push({
      source: src,
      destination: dst,
      score: maxSourceScore,
    })
  }

  // Now that we have all the matches, go back through our list of duplicate destinations. If any
  // of the duplicates matched a different source, we can eliminate it from the list of duplicates.
  // If that leaves only a single destination, then we can assume that destination is the match
  // for the source, and add it to the list of matches.
  for (const duplicateDst of duplicateDestinations) {
    const { source, destinations: duplicateDsts } = duplicateDst
    // Remove destinations that are matched by a different source
    const unmatchedDestinations = duplicateDsts.filter(
      (dst) => !matches.some((match) => match.destination === dst)
    )
    // Remove this duplicateDestination. We'll add it back if there are still multiple duplicates
    // after filtering.
    duplicateDestinations = duplicateDestinations.filter((duplicate) => duplicate !== duplicateDst)
    // If only a single destination remains, consider this a match instead
    if (unmatchedDestinations.length === 1) {
      const remainingDestination = unmatchedDestinations[0]
      matches.push({
        source,
        destination: remainingDestination,
        score: getScore(source, remainingDestination),
      })
    } else {
      duplicateDestinations.push({ source, destinations: unmatchedDestinations })
    }
  }

  // If there are line items in Source that are unmatched, not duplicate sources,
  // and don't have duplicate destinations, they are extra sources
  const extraSources = filteredSources.filter(
    (src) =>
      !matches.some((match) => match.source === src) &&
      !duplicateSources.some((duplicate) => duplicate.sources.includes(src)) &&
      !duplicateDestinations.some((duplicate) => duplicate.source === src)
  )

  // De-duplicate the duplicate arrays, because we might add them multiple times when processing
  // destinations.
  const finalDuplicateSources = uniqueBy(duplicateSources, (duplicate) => duplicate.destination)
  const finalDuplicateDestinations = uniqueBy(
    duplicateDestinations,
    (duplicate) => duplicate.source
  )

  return {
    matches,
    duplicateSources: finalDuplicateSources,
    duplicateDestinations: finalDuplicateDestinations,
    extraSources,
    extraDestinations,
    emptySources,
    emptyDestinations,
  }
}

type LineItemGroupIdentifier = {
  name: string
  code: string | null
}

type GetLineItemGroupMatchesParams<
  Source extends LineItemGroupIdentifier,
  Destination extends LineItemGroupIdentifier,
> = {
  sources: Source[]
  destinations: Destination[]
}

/**
 * Computes the best matches given an array of source line item groups and an array of destination line item groups.
 */
export function getLineItemGroupMatches<
  Source extends LineItemGroupIdentifier,
  Destination extends LineItemGroupIdentifier,
>({
  sources,
  destinations,
}: GetLineItemGroupMatchesParams<Source, Destination>): MatchResult<Source, Destination> {
  const isEmpty = (group: LineItemGroupIdentifier): boolean => {
    const isNameEmpty = normalizeString(group.name).length === 0
    const isCodeEmpty = !group.code || normalizeString(group.code).length === 0
    return isCodeEmpty && isNameEmpty
  }
  return getMatches({ sources, destinations, isEmpty, getScore: getLineItemGroupScore })
}

/**
 * Computes the best matches given an array of source line items and an array of destination line items.
 * This algorithm does not throw. You must handle errors in the caller (eg: if you want to prevent extra sources).
 *
 * The algorithm is described in depth here:
 * https://www.notion.so/siteline/Integration-Line-Item-Matching-d242626db6a14d4e82dba8449d7a1f40
 *
 * Note that we use generics so that each integration can attach more data to the source/destination,
 * like a puppeteer element handle (GCPay) or an invoice data row (Textura).
 */
export function getLineItemMatches<
  Source extends LineItemIdentifier,
  Destination extends LineItemIdentifier,
>(
  sources: Source[],
  destinations: Destination[],
  scoring: LineItemScoring
): MatchResult<Source, Destination> {
  // Add each empty source and destination to emptySources and emptyDestinations
  const isEmpty = (lineItem: LineItemIdentifier): boolean => {
    const isCodeEmpty = normalizeString(lineItem.code).length === 0 || scoring.code === 'zero'
    const isNameEmpty = normalizeString(lineItem.name).length === 0 || scoring.name === 'zero'
    const isTotalValueEmpty = lineItem.totalValue === 0 || scoring.totalValue === 'zero'
    return isCodeEmpty && isNameEmpty && isTotalValueEmpty
  }

  return getMatches({
    sources,
    destinations,
    isEmpty,
    getScore: (src, dst) => getLineItemScore(src, dst, scoring),
  })
}

/**
 * Integrations in different groups may provide data for and manage different
 * parts of the billing process.
 */
export enum IntegrationTypeFamily {
  GC_PORTAL = 'GC_PORTAL',
  ERP = 'ERP',
}

export function getIntegrationTypeFamily(type: IntegrationType): IntegrationTypeFamily {
  switch (type) {
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
      return IntegrationTypeFamily.GC_PORTAL
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return IntegrationTypeFamily.ERP
  }
}

export type CommonIntegrationWithMappings = {
  type: IntegrationType
  // Use Record<K,V> for compatibility with both node and web code. We cast to the correct mappings
  // where this is used anyways.
  mappings: Record<string, unknown>
}
export function getIntegrationProjectId(integration: CommonIntegrationWithMappings): string | null {
  switch (integration.type) {
    case IntegrationType.TEXTURA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsTextura
      return mappings.project.texturaProjectId ?? null
    }
    case IntegrationType.PROCORE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcore
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.PROCORE_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcorePeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.FOUNDATION_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsFoundationPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.FOUNDATION: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsFoundation
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE: {
      const mappings = integration.mappings as
        | integrationTypes.IntegrationMappingsSage300Cre
        | integrationTypes.IntegrationMappingsSage100Contractor
      return mappings.project.hh2ProjectId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsSage100ContractorAgave
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsSage100ContractorPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.SAGE_300_CRE_AGAVE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSage300CreAgave
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SPECTRUM: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsViewpointSpectrum
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SPECTRUM_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsViewpointSpectrumPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.VISTA_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsViewpointVistaPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.VISTA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsViewpointVista
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.GC_PAY: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsGcPay
      return mappings.project.gcPayProjectId ?? null
    }
    case IntegrationType.ACUMATICA_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsAcumaticaPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.ACUMATICA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsAcumatica
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SAGE_INTACCT: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSageIntacct
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.SAGE_INTACCT_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSageIntacctPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.CMIC_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmicPeyote
      return mappings.project.peyoteProjectId ?? null
    }
    case IntegrationType.CMIC: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmic
      return mappings.project.agaveProjectId ?? null
    }
    case IntegrationType.QUICKBOOKS_ONLINE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsQuickbooksOnline
      return mappings.project.quickbooksProjectId ?? null
    }
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsQuickbooksOnlinePeyote
      return mappings.project.quickbooksProjectId ?? null
    }
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return null
  }
}

/**
 * Returns the integration project IDs whose imported vendor invoices belong to a contract.
 * Most integrations map a contract to a single project. Two patterns extend that:
 *   - Vista contracts configured with `MULTIPLE_PROJECTS_ONE_CONTRACT` have no primary project;
 *     the Vista jobs live on `associatedProjectIds` instead.
 *   - Sage 300 CRE customers with "combo jobs" (one G&A Job + one subcontract Job per physical
 *     project) map the primary Job on `project.hh2ProjectId` and any related Jobs on
 *     `associatedProjectIds`, populated automatically at project import time.
 * The result is deduped and combines both sources. Returns an empty array when no project scope
 * can be determined, so callers can avoid widening a query past this contract.
 */
export function getVendorInvoiceIntegrationProjectIds(
  integration: CommonIntegrationWithMappings
): string[] {
  const integrationProjectId = getIntegrationProjectId(integration)
  const associatedProjectIds = getAssociatedIntegrationProjectIds(integration)
  const ids = [...(integrationProjectId ? [integrationProjectId] : []), ...associatedProjectIds]
  // Preserve insertion order (primary first) while removing duplicates
  return [...new Set(ids)]
}

/**
 * Returns extra integration project IDs stored on the mapping beyond the primary `project` ID.
 * Only integrations that opt in to rolling up multiple integration jobs into one Siteline
 * contract have anything here. Kept separate from `getVendorInvoiceIntegrationProjectIds` so
 * callers that care about "just the extras" (e.g. UI badges) don't have to dedupe.
 */
function getAssociatedIntegrationProjectIds(integration: CommonIntegrationWithMappings): string[] {
  switch (integration.type) {
    case IntegrationType.VISTA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsViewpointVista
      return mappings.associatedProjectIds ?? []
    }
    case IntegrationType.SAGE_300_CRE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSage300Cre
      return mappings.associatedProjectIds ?? []
    }
    case IntegrationType.SAGE_300_CRE_AGAVE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSage300CreAgave
      return mappings.associatedProjectIds ?? []
    }
    case IntegrationType.VISTA_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsViewpointVistaPeyote
      return mappings.associatedProjectIds ?? []
    }
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.TEST:
      return []
  }
}

export function getIntegrationContractId(
  integration: CommonIntegrationWithMappings
): string | null {
  switch (integration.type) {
    case IntegrationType.TEXTURA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsTextura
      return mappings.contract.texturaContractId ?? null
    }
    case IntegrationType.PROCORE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcore
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.PROCORE_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcorePeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE: {
      const mappings = integration.mappings as
        | integrationTypes.IntegrationMappingsSage300Cre
        | integrationTypes.IntegrationMappingsSage100Contractor
      return mappings.contract.hh2ContractId ?? null
    }
    case IntegrationType.GC_PAY: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsGcPay
      return mappings.contract.gcPaySovId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsSage100ContractorAgave
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsSage100ContractorPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.SAGE_300_CRE_AGAVE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSage300CreAgave
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.VISTA_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsViewpointVistaPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.VISTA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsViewpointVista
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.SPECTRUM: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsViewpointSpectrum
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.SPECTRUM_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsViewpointSpectrumPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.ACUMATICA_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsAcumaticaPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.ACUMATICA: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsAcumatica
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.FOUNDATION_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsFoundationPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.FOUNDATION: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsFoundation
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.SAGE_INTACCT: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSageIntacct
      return mappings.contract.agaveContractId ?? null
    }
    case IntegrationType.SAGE_INTACCT_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSageIntacctPeyote
      return mappings.contract.peyoteContractId ?? null
    }
    case IntegrationType.CMIC_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmicPeyote
      return mappings.contract?.cmicContractCode ?? null
    }
    case IntegrationType.CMIC: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmic
      return mappings.contract?.cmicContractCode ?? null
    }
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return null
  }
}

/**
 * Returns the contract number from integration mappings
 */
export function getIntegrationContractNumber(
  integration: CommonIntegrationWithMappings
): string | null {
  const mappings = integration.mappings as integrationTypes.IntegrationMappings
  switch (mappings.type) {
    case 'textura':
    case 'procorePeyote':
    case 'procore':
    case 'foundationPeyote':
    case 'foundation':
    case 'sage300Cre':
    case 'sage100Contractor':
    case 'sage100ContractorAgave':
    case 'sage100ContractorPeyote':
    case 'sage300CreAgave':
    case 'spectrumPeyote':
    case 'spectrum':
    case 'vista':
    case 'vistaPeyote':
    case 'gcPay':
    case 'acumatica':
    case 'acumaticaPeyote':
    case 'sageIntacct':
    case 'sageIntacctPeyote':
    case 'cmicPeyote':
    case 'cmic':
      return mappings.contract?.contractNumber ?? null
    case 'test':
    case 'file':
    case 'quickbooksOnline':
    case 'quickbooksOnlinePeyote':
      // No contract number for Quickbooks Online
      return null
  }
}

/**
 * Returns the project number from integration mappings
 */
export function getIntegrationProjectNumber(
  integration: CommonIntegrationWithMappings
): string | null {
  const mappings = integration.mappings as integrationTypes.IntegrationMappings
  switch (mappings.type) {
    case 'textura':
    case 'procorePeyote':
    case 'procore':
    case 'foundationPeyote':
    case 'foundation':
    case 'sage300Cre':
    case 'sage100Contractor':
    case 'sage100ContractorAgave':
    case 'sage100ContractorPeyote':
    case 'sage300CreAgave':
    case 'spectrumPeyote':
    case 'spectrum':
    case 'vista':
    case 'vistaPeyote':
    case 'gcPay':
    case 'acumatica':
    case 'acumaticaPeyote':
    case 'sageIntacct':
    case 'sageIntacctPeyote':
    case 'cmicPeyote':
    case 'cmic':
    case 'quickbooksOnline':
    case 'quickbooksOnlinePeyote':
      return mappings.project.projectNumber ?? null
    case 'test':
    case 'file':
      return null
  }
}

export function getIntegrationAssociatedCompanyId(
  integration: CommonIntegrationWithMappings
): string | null {
  switch (integration.type) {
    case IntegrationType.PROCORE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcore
      return mappings.associatedCompanyId ?? null
    }
    case IntegrationType.PROCORE_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsProcorePeyote
      return mappings.peyoteAssociatedCompanyId ?? null
    }
    case IntegrationType.TEXTURA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.GC_PAY:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return null
  }
}

/**
 * Returns the invoice number from a write sync payload.
 */
export function getInvoiceNumberFromPayload(
  payload: integrationTypes.WriteSyncPayload
): string | null {
  switch (payload.type) {
    case 'payAppFoundation':
      return payload.invoiceNumber ?? null
    case 'payAppSage100':
      return payload.invoiceCode
    case 'payAppLineItemsSage300':
      return payload.invoiceCode
    case 'payAppLineItemsSpectrum':
      return payload.invoiceCode
    case 'payAppLineItemsVista':
      return payload.invoiceCode ?? null
    case 'payAppLineItemsSageIntacct':
      return payload.invoiceCode ?? null
    case 'payAppQuickbooks':
      return payload.invoiceNumber ?? null
    case 'payAppCmic':
      return payload.invoiceCode
    case 'payAppLineItemsQuickbooksOnline':
    case 'payAppQuickbooksOnline':
      return payload.invoiceCode ?? null
    case 'payAppTextura':
    case 'payAppGcPay':
    case 'payAppProcore':
    case 'payAppLineItemsAcumatica':
    case 'payAppManual':
    case 'payAppFoundationFileGenie':
    case 'payAppFoundationFileFsi':
    case 'payAppComputerEase':
    case 'lienWaivers':
    case 'legalRequirement':
    case 'payAppNetSuite':
    case 'changeOrderRequestVista':
      return null
  }
}

type IntegrationCustomer = {
  id: string
  name: string
}

export function getIntegrationCustomer(
  integration: CommonIntegrationWithMappings
): IntegrationCustomer | null {
  switch (integration.type) {
    case IntegrationType.SAGE_300_CRE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSage300Cre
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.hh2CustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE: {
      const mappings = integration.mappings as
        | integrationTypes.IntegrationMappingsViewpointVistaPeyote
        | integrationTypes.IntegrationMappingsViewpointSpectrumPeyote
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.peyoteCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.ACUMATICA_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsAcumaticaPeyote
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.peyoteCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_300_CRE_AGAVE: {
      const mappings = integration.mappings as
        | integrationTypes.IntegrationMappingsViewpointVista
        | integrationTypes.IntegrationMappingsViewpointSpectrum
        | integrationTypes.IntegrationMappingsAcumatica
        | integrationTypes.IntegrationMappingsFoundation
        | integrationTypes.IntegrationMappingsSageIntacct
        | integrationTypes.IntegrationMappingsSage100ContractorAgave
        | integrationTypes.IntegrationMappingsSage300CreAgave
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.agaveCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.CMIC_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmicPeyote
      if (!mappings.customer || !mappings.customer.cmicCustomerCode || !mappings.customer.name) {
        return null
      }
      return {
        id: mappings.customer.cmicCustomerCode,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.CMIC: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsCmic
      if (!mappings.customer || !mappings.customer.cmicCustomerCode || !mappings.customer.name) {
        return null
      }
      return {
        id: mappings.customer.cmicCustomerCode,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.QUICKBOOKS_ONLINE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsQuickbooksOnline
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.quickbooksCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsQuickbooksOnlinePeyote
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.quickbooksCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE: {
      const mappings =
        integration.mappings as integrationTypes.IntegrationMappingsSage100ContractorPeyote
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.peyoteCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.SAGE_INTACCT_PEYOTE: {
      const mappings = integration.mappings as integrationTypes.IntegrationMappingsSageIntacctPeyote
      if (!mappings.customer) {
        return null
      }
      return {
        id: mappings.customer.peyoteCustomerId,
        name: mappings.customer.name,
      }
    }
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return null
  }
}

/**
 * Whether an integration supports the specified billing type.
 */
export function supportsBillingType(type: IntegrationType, billingType: BillingType): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
      return billingType === BillingType.LUMP_SUM
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return [
        BillingType.LUMP_SUM,
        BillingType.UNIT_PRICE,
        BillingType.TIME_AND_MATERIALS,
      ].includes(billingType)
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      // NOTE that CMiC doesn't pull SOVs
      // and we can only post invoices for the total amount due
      // so it supports all billing types by default
      return [
        BillingType.LUMP_SUM,
        BillingType.UNIT_PRICE,
        BillingType.TIME_AND_MATERIALS,
      ].includes(billingType)
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.NETSUITE_FILE:
      // NOTE that NetSuite could support time and materials if we update the export to export
      // a lump sum amount for the amount billed
      return [BillingType.LUMP_SUM, BillingType.UNIT_PRICE].includes(billingType)
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.TEST:
      return true
  }
}

/**
 * Whether an integration is ready to be used for onboarding new projects.
 */
export function supportsProjectOnboarding(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return true
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      // File-based systems don't support project onboarding of any billing
      return false
  }
}

/**
 * Whether an integration tolerates a negative tax rate. Foundation customers use one as a
 * workaround for a contract-level deduction (e.g. a 1% gross receipts levy on government jobs),
 * which Foundation posts as a negative sales tax. No other ERP accepts it.
 * See https://sitelinetech.slack.com/archives/CMY20J4S3/p1767636620223329
 */
export function supportsNegativeTaxPercent(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
      return true
    case IntegrationType.ACUMATICA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA:
    case IntegrationType.VISTA_PEYOTE:
      return false
  }
}

/** Whether an integration supports reading tax groups */
export function supportsReadingTaxGroups(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
      return true
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      // Note that we read tax details from Sage Intacct. The advanced tax functionality in Intacct
      // is quite complicated, and future customers may not want to use individual tax details in
      // Siteline - there's a concept of tax schedules in Intacct, which are groupings of tax
      // details with a total tax rate. The Intacct API doesn't expose tax schedules, so we can only
      // support importing tax details.
      return true
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports using the mappings from `TaxGroup.integrationMappings`.
 * This is the new way of linking hh2/agave tax groups to Siteline.
 * Instead of having a static hh2/agave tax group ID on the contract, Siteline now has its own
 * tax groups which contain a list of integration mappings (one to many).
 * During a sync, we look for `contract.defaultTaxGroup.integrationMappings` and extract the hh2/agave
 * tax group ID to use on the invoice.
 */
export function supportsLinkingTaxGroup(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true

    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports reading data from the 3rd-party service.
 */
export function supportsReadSync(
  type: IntegrationType,
  readType: 'changeOrders' | 'sov' | 'vendors'
): boolean {
  switch (readType) {
    case 'changeOrders': {
      switch (type) {
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.GC_PAY:
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
          return true
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
          return true
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return false
      }
      break
    }
    case 'sov': {
      switch (type) {
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.GC_PAY:
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return true
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
          return false
      }
      break
    }
    case 'vendors': {
      switch (type) {
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.TEXTURA:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.CMIC:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return true
        // If any new GC portals add support for reading vendors, update the corresponding switch
        // case in `ProjectSettingsVendorList`
        case IntegrationType.GC_PAY:
        case IntegrationType.TEST:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.NETSUITE_FILE: {
          return false
        }
      }
    }
  }
}

/**
 * Whether the integration can filter its vendor list by name in the ERP query itself, so a vendor
 * picker can search on demand instead of loading every vendor up front (CMiC vendor masters run to
 * thousands of records and take minutes to page).
 *
 * Returning false is always safe: the caller reads the full list and filters it in memory instead.
 */
export function supportsIntegrationVendorSearch(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.CMIC_PEYOTE:
      return true
    // Agave-backed CMiC has no passthrough vendor search, so it reads the whole list like the rest
    case IntegrationType.CMIC:
    case IntegrationType.ACUMATICA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA:
    case IntegrationType.VISTA_PEYOTE:
      return false
  }
}

/**
 * Whether the integration can filter its project list by name or number in the ERP query itself, so
 * a project picker can search on demand instead of loading every project up front (CMiC job tables
 * run to thousands of rows and are slow to page).
 *
 * Returning false is always safe: the caller reads the full list and filters it in memory instead.
 * Note this also decides whether the project picker offers a created-date filter — the name search
 * replaces it, so an integration returning true shows no date range.
 */
export function supportsIntegrationProjectSearch(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.CMIC_PEYOTE:
      return true
    // Agave-backed CMiC has no passthrough job search, so it reads the whole list like the rest
    case IntegrationType.CMIC:
    case IntegrationType.ACUMATICA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA:
    case IntegrationType.VISTA_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports writing data to the 3rd-party service.
 */
export function supportsWriteSync(
  integrationType: IntegrationType,
  syncType: 'payApp' | 'payAppLineItems' | 'lienWaivers' | 'legalRequirement' | 'changeOrderRequest'
): boolean {
  switch (syncType) {
    case 'payApp':
      switch (integrationType) {
        case IntegrationType.TEXTURA:
        case IntegrationType.GC_PAY:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.TEST:
        case IntegrationType.CMIC:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return true
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.NETSUITE_FILE:
          return false
      }
      break
    case 'payAppLineItems': {
      switch (integrationType) {
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return true
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.GC_PAY:
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
          return false
      }
      break
    }
    case 'lienWaivers': {
      switch (integrationType) {
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.GC_PAY:
          return true
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return false
      }
      break
    }
    case 'legalRequirement': {
      switch (integrationType) {
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.GC_PAY:
          return true
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return false
      }
      break
    }
    case 'changeOrderRequest': {
      switch (integrationType) {
        case IntegrationType.VISTA_PEYOTE:
        case IntegrationType.VISTA:
          return true
        case IntegrationType.TEXTURA:
        case IntegrationType.TEST:
        case IntegrationType.GC_PAY:
        case IntegrationType.ACUMATICA_PEYOTE:
        case IntegrationType.ACUMATICA:
        case IntegrationType.SAGE_100_CONTRACTOR:
        case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
        case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
        case IntegrationType.SAGE_300_CRE:
        case IntegrationType.SAGE_300_CRE_AGAVE:
        case IntegrationType.SPECTRUM_PEYOTE:
        case IntegrationType.SPECTRUM:
        case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
        case IntegrationType.FOUNDATION_FILE:
        case IntegrationType.FOUNDATION_PEYOTE:
        case IntegrationType.FOUNDATION:
        case IntegrationType.PROCORE_PEYOTE:
        case IntegrationType.PROCORE:
        case IntegrationType.COMPUTER_EASE_FILE:
        case IntegrationType.SAGE_INTACCT_PEYOTE:
        case IntegrationType.SAGE_INTACCT:
        case IntegrationType.CMIC_PEYOTE:
        case IntegrationType.CMIC:
        case IntegrationType.NETSUITE_FILE:
        case IntegrationType.QUICKBOOKS_ONLINE:
        case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
          return false
      }
      break
    }
  }
}

// Single source of truth for bulk-write-sync support. `satisfies Record<...>`
// forces every IntegrationType to be classified, and BulkSyncIntegrationType is
// derived from the `true` entries — so the type and the runtime check can never drift.
const BULK_SYNC_SUPPORT = {
  [IntegrationType.FOUNDATION]: true,
  [IntegrationType.FOUNDATION_PEYOTE]: true,
  [IntegrationType.SAGE_100_CONTRACTOR]: true,
  [IntegrationType.SAGE_100_CONTRACTOR_AGAVE]: true,
  [IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]: true,
  [IntegrationType.CMIC]: true,
  [IntegrationType.CMIC_PEYOTE]: true,
  [IntegrationType.SAGE_300_CRE]: false,
  [IntegrationType.SAGE_300_CRE_AGAVE]: false,
  [IntegrationType.ACUMATICA]: false,
  [IntegrationType.ACUMATICA_PEYOTE]: false,
  [IntegrationType.VISTA_PEYOTE]: false,
  [IntegrationType.VISTA]: false,
  [IntegrationType.SPECTRUM]: false,
  [IntegrationType.SPECTRUM_PEYOTE]: false,
  [IntegrationType.GC_PAY]: false,
  [IntegrationType.TEXTURA]: false,
  [IntegrationType.TEST]: false,
  [IntegrationType.QUICKBOOKS_ENTERPRISE_FILE]: false,
  [IntegrationType.FOUNDATION_FILE]: false,
  [IntegrationType.COMPUTER_EASE_FILE]: false,
  [IntegrationType.PROCORE_PEYOTE]: false,
  [IntegrationType.PROCORE]: false,
  [IntegrationType.SAGE_INTACCT]: false,
  [IntegrationType.SAGE_INTACCT_PEYOTE]: false,
  [IntegrationType.NETSUITE_FILE]: false,
  [IntegrationType.QUICKBOOKS_ONLINE]: false,
  [IntegrationType.QUICKBOOKS_ONLINE_PEYOTE]: false,
} satisfies Record<IntegrationType, boolean>

export type BulkSyncIntegrationType = {
  [Type in IntegrationType]: (typeof BULK_SYNC_SUPPORT)[Type] extends true ? Type : never
}[IntegrationType]

export function supportsBulkWriteSync(
  integrationType: IntegrationType
): integrationType is BulkSyncIntegrationType {
  return BULK_SYNC_SUPPORT[integrationType]
}

type SupportsInvoiceAutoCodeArgs =
  | {
      integrationType: Exclude<
        IntegrationType,
        IntegrationType.SAGE_INTACCT | IntegrationType.SAGE_INTACCT_PEYOTE
      >
    }
  | {
      integrationType: IntegrationType.SAGE_INTACCT | IntegrationType.SAGE_INTACCT_PEYOTE
      companyIntegrationMetadata: CompanyIntegrationMetadata | undefined
    }

/** Whether an integration supports automatically generating a code when creating invoices */
export function supportsInvoiceAutoCode(args: SupportsInvoiceAutoCodeArgs): boolean {
  switch (args.integrationType) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false

    // NOTE that Sage Intacct sometimes automatically generates a code and sometimes doesn't
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_INTACCT_PEYOTE: {
      if (!args.companyIntegrationMetadata) {
        throw Error('Missing integration metadata for Sage Intacct integration')
      }
      const sageIntacctMetadata =
        args.companyIntegrationMetadata as CompanyIntegrationMetadataSageIntacct
      return sageIntacctMetadata.hasAutoNumberingEnabled ?? true
    }
  }
}

/** Whether we are able to guess the latest invoice code when creating invoices */
export function supportsGeneratingInvoiceCode(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE:
      return true

    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports passing a manual invoice code,
 * as opposed to generating it automatically every time.
 */
export function supportsManualInvoiceCode(
  integrationType: IntegrationType,
  companyIntegrationMetadataSageIntacct?: CompanyIntegrationMetadataSageIntacct
): boolean {
  switch (integrationType) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return true

    // NOTE that Sage Intacct sometimes automatically generates a code and sometimes doesn't
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      if (!companyIntegrationMetadataSageIntacct) {
        throw Error('Missing integration metadata for Sage Intacct integration')
      }
      return !companyIntegrationMetadataSageIntacct.hasAutoNumberingEnabled

    // Acumatica generates invoice codes automatically and they cannot be manually entered.
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 *  Whether an integration supports reading vendors from the 3rd-party service
 */
export function supportsVendorRefresh(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsBillingOptOut(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports reading vendor invoices from the 3rd-party service. This is
 * typically used only on ERP systems.
 *
 * All integrations that return true should also return a boolean below in the corresponding
 * `supportsVendorInvoicesDateRangeFilter` util.
 */
export function supportsVendorInvoices(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * For integrations that support reading vendor invoices, some allow for filtering by date
 * while others only allow querying by job. When querying by date is possible, the API requires
 * a date range be provided to optimize performance. When query by date is not possible, a date
 * range should not be provided so the larger data set isn't re-queried repeatedly.
 */
export function supportsVendorInvoicesDateRangeFilter(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
      return false
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.NETSUITE_FILE: {
      const name = getIntegrationName(type)
      throw new Error(`Reading vendor invoices is not yet supported with ${name}`)
    }
  }
}

/**
 * For integrations that support reading vendor invoices, some allow for filtering by vendor name
 * when the Siteline vendor isn't linked to a vendor in the integration. When that's possible, we
 * still show the button for calculating the invoice amount from ERP, and search by name. To figure
 * out if an invoice can support this, look through the source data returned for an AP invoice and
 * see if the full vendor name is included anywhere - if so, try including a filter by that source
 * data field in the AP invoices request.
 */
export function supportsVendorInvoicesVendorNameFilter(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.NETSUITE_FILE: {
      const name = getIntegrationName(type)
      throw new Error(`Reading vendor invoices is not yet supported with ${name}`)
    }
  }
}

export function supportsMarkAsSynced(integrationType: IntegrationType): boolean {
  const isGcPortalIntegration =
    getIntegrationTypeFamily(integrationType) === IntegrationTypeFamily.GC_PORTAL
  const supportsSync = supportsWriteSync(integrationType, 'payApp')
  return isGcPortalIntegration && supportsSync
}

export function supportsBulkImport(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.TEXTURA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether bulk AP invoice imports are supported.
 */
export function supportsBulkVendorInvoiceImport(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
      return true
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.CMIC:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether `ImportedVendorInvoice.integrationContractId` is resolved during a bulk AP invoice import.
 * We use this to determine whether to filter on contract ID (in addition to project ID) when loading
 * imported invoices from the API.
 */
export function doesBulkVendorInvoiceImportResolveIntegrationContractId(
  type: IntegrationType
): boolean {
  switch (type) {
    // These support bulk AP import, but integration contract IDs are not available during the
    // import. For CMiC that's because a voucher doesn't carry its contract code.
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.CMIC_PEYOTE:
      return false

    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.CMIC:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsReadingVendorContacts(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true

    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return false

    // The following integrations can read vendors, but not their contacts.
    // For Textura specifically, it's because contact emails are not exposed.
    case IntegrationType.TEXTURA:
      return false

    // The following integrations don't have support for reading vendors at all
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether a specific integration supports reading cost data for over/under billing.
 */
export function supportsReadingCost(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsReadingCostCodes(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsReadingCostTypes(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsWritingChangeOrderRequests(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration needs credentials to function. File-based integrations
 * have no real credentials, so they don't require any — the admin status chip
 * stays green without anything being set.
 */
export function requiresCredential(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.TEST:
      return false
  }
}

export function supportsRetentionTrackingLevel(
  integrationType: IntegrationType,
  trackingLevel: RetentionTrackingLevel
): boolean {
  switch (integrationType) {
    case IntegrationType.GC_PAY:
      return [
        RetentionTrackingLevel.STANDARD,
        RetentionTrackingLevel.LINE_ITEM,
        RetentionTrackingLevel.PROJECT,
        RetentionTrackingLevel.NONE,
      ].includes(trackingLevel)
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
      return [RetentionTrackingLevel.STANDARD, RetentionTrackingLevel.NONE].includes(trackingLevel)
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
  }
}

export function supportsReadingLineItems(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return true
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

export function supportsReadingPayments(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.TEST:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether we can read the AR invoices that already exist in the ERP for a contract (used to warn
 * before syncing that a duplicate invoice may be created). Only supported for Vista today; every
 * other integration falls back to warning only when the invoice was already synced from Siteline's
 * perspective. The `GET /ar-invoices` endpoint throws for integrations where this is false, so keep
 * this in sync with the dispatch in `routes/ar-invoices.ts`.
 */
export function supportsReadingArInvoices(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.TEXTURA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether `GET /ar-invoices` returns per-line amounts (`lineItems`) for the integration, so a
 * Siteline pay app can be compared line by line against the ERP invoice for the same period. Kept
 * separate from `supportsReadingArInvoices` because that guard drives the pre-sync duplicate-invoice
 * warning in the web app, which doesn't apply to GC portals (the draw is expected to exist) and
 * would make every Textura sync fetch every draw first.
 */
export function supportsReadingArInvoiceLineItems(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
      return true
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

export function isAgaveIntegration(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM:
    case IntegrationType.PROCORE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return false
  }
}

export function supportsAgaveDebuggerLink(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION:
      return true
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
      return false
  }
}

/**
 * Whether this integration type is backed by a Siteline-hosted SQL Server
 * connection that the admin-dashboard's Peyote SQL debugger can introspect
 * (network reachability, login, role/permission listing).
 */
export function supportsPeyoteSqlDebugger(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
      return true
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function usesAgaveConnector(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return true
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.TEXTURA:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function isHh2Integration(
  type: IntegrationType
): type is IntegrationType.SAGE_100_CONTRACTOR | IntegrationType.SAGE_300_CRE {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return false
  }
}

/**
 * Whether the integration is a Peyote (Siteline) integration.
 */
export function isPeyoteIntegration(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.FOUNDATION_PEYOTE:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
      return false
  }
}

/**
 * Whether or not to expect a delay in data entered in the integration service appearing in the
 * results of Siteline requests
 */
export function doesIntegrationHaveDataDelay(type: IntegrationType): boolean {
  if (isHh2Integration(type)) {
    // Hh2 requests hit a sync client that fetches data from the ERPs' databases on a regular schedule,
    // so there can be a delay in data from the ERP showing up in hh2's results.
    return true
  }
  if (isAgaveIntegration(type)) {
    // Agave communicates directly with the ERPs' databases, so there is no delay.
    return false
  }
  // Other endpoints don't have a known delay because we either interface with the service directly
  // or they are file-based
  return false
}

/**
 * Translate integration type to submit via filter type so that it can be used in
 * the submit via filter.
 */
export function getSubmitViaTypeFromIntegrationType(
  type: IntegrationType
): ContractListSubmitViaFilter | null {
  switch (type) {
    case IntegrationType.GC_PAY:
      return ContractListSubmitViaFilter.GC_PAY
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
      return ContractListSubmitViaFilter.PROCORE
    case IntegrationType.TEXTURA:
      return ContractListSubmitViaFilter.TEXTURA
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return null
  }
}

/**
 * Whether an integration supports setting a customer mapping.
 */
export function supportsSettingCustomer(type: IntegrationType): boolean {
  switch (type) {
    // The following integrations might not have a default customer on their contract, so we allow
    // setting a customer mapping to ensure invoices can be created.
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.TEST:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports getting customers for the project and contract only
 */
export function supportsGettingCustomersForProjectAndContractOnly(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.TEST:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports tax calculation.
 */
export function supportsReadTaxCalculationType(
  type: IntegrationType,
  taxCalculationType: TaxCalculationType
): boolean {
  switch (type) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      // Vista supports reading both single and multiple tax calculation types
      return true
    case IntegrationType.SAGE_300_CRE_AGAVE:
      // Sage 300 supports reading both single and multiple tax calculation types, but only via
      // Agave (as hh2 does not seem to provide the line item tax detail)
      return true
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
      // Sage currently supports reading only single tax group by contract
      return taxCalculationType !== TaxCalculationType.MULTIPLE_TAX_GROUPS
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
      return taxCalculationType === TaxCalculationType.MULTIPLE_TAX_GROUPS
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
      return true
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.TEST:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports tax calculation.
 */
export function supportsWriteTaxCalculation(
  type: IntegrationType,
  taxCalculationType: TaxCalculationType = TaxCalculationType.SINGLE_TAX_GROUP
): boolean {
  switch (type) {
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
      // These integrations support writing invoices with single or multiple tax calculation types
      return true
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
      // CMiC only supports a single tax group per invoice, so we always sync with the default tax group
      // (and added a warning on the CMiC sync dialog to make sure the user knows this)

      // We can only designate a single tax group when exporting to ComputerEase at the invoice
      // level, so we can't support exporting with multiple tax groups per contract

      // Foundation only allows syncing one tax group per invoice
      return taxCalculationType === TaxCalculationType.SINGLE_TAX_GROUP
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
      return taxCalculationType === TaxCalculationType.MULTIPLE_TAX_GROUPS
    case IntegrationType.GC_PAY:
    case IntegrationType.TEXTURA:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration supports credentials auto-rotation.
 */
export function supportsCredentialsAutoRotation(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether the integration supports reading line item groups
 */
export function supportsLineItemGroups(
  type: IntegrationType,
  companyIntegrationMetadata: CompanyIntegrationMetadata | undefined
): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
      return true
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      // Sage 100 doesn't natively support line item grouping, but we may group line items by
      // proposal if multiple proposals exist on a contract in Sage
      return true
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      // Sage 300 doesn't natively support line item grouping, but we may group line items by job
      // for combo jobs if the company integration has the corresponding setting enabled
      return true
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      // Sage Intacct doesn't natively support line item grouping that we're aware of, but we may
      // group line items by job for combo jobs if the company integration has a corresponding
      // setting enabled
      return true
    case IntegrationType.VISTA_PEYOTE: {
      // Vista supports reading bill groups from line items. If they are valid (contiguous), we
      // will group them together in the SOV. If they are invalid, we will not group them.
      const metadata = companyIntegrationMetadata as CompanyIntegrationMetadataVistaPeyote
      return metadata.groupLineItemsByBillGroup ?? false
    }
    case IntegrationType.VISTA:
      // Vista supports reading bill groups from line items. If they are valid (contiguous), we
      // will group them together in the SOV. If they are invalid, we will not group them.
      return (
        (companyIntegrationMetadata as CompanyIntegrationMetadataVista).groupLineItemsByBillGroup ??
        false
      )
    // If read access to table CR_BID_ITEM_DES_MC is enabled, we will create line item groups
    // from the Bill_Group_Code (groupCode) and Bill_Group_Description (groupName) fields.
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether the integration supports reading SOVs with change orders as separate line items. This
 * corresponds to whether it should be allowed to set the company integration
 * `importChangeOrdersMethod` to 'separate-line-items' (on the company integration metadata).
 */
export function supportsImportingSeparateChangeOrderLineItems(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether an integration project ID is required when importing a project. Some integrations
 * (e.g. Vista) only require a contract ID and the project ID is optional. Additionally, combo
 * job imports using MULTIPLE_PROJECTS_ONE_CONTRACT do not require a project ID.
 */
export function requiresIntegrationProjectId(
  type: IntegrationType,
  comboJobMethod: ImportIntegrationComboJobMethod
): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.TEST:
      return true
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return comboJobMethod !== ImportIntegrationComboJobMethod.MULTIPLE_PROJECTS_ONE_CONTRACT
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether an integration contract ID is required when importing a project. Some integrations
 * (e.g. Foundation, Sage Intacct) allow projects to be imported without a contract.
 */
export function requiresIntegrationContractId(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.TEXTURA:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.PROCORE:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.TEST:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether the integration supports multiple ERP contracts, which is essentially determined by
 * whether write syncs support line item allocation (so that we know where to write for each pay app progress).
 * If a write sync payload contains "payAppLineItems" (it's part of WriteSyncPayloadPayAppLineItems),
 * then the integration supports multiple ERP contracts.
 */
export function supportsMultipleErpContracts(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return true
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
      return false
    // Multiple ERP contracts is currently banned for Quickbooks Online
    // to make it easier to handle multiple sync types (total amount and line items)
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

/**
 * Whether the integration supports reading integration combo jobs using different methods. Combo
 * jobs are integration projects where line items link to multiple jobs. Currently we only support
 * special handling for combo jobs in Sage 300.
 */
export function supportedComboJobImportMethodsForIntegration(
  type: IntegrationType
): ImportIntegrationComboJobMethod[] {
  switch (type) {
    case IntegrationType.SAGE_300_CRE:
      return [
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_FLAT_SOV,
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_GROUPED_SOV,
      ]
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return [
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_FLAT_SOV,
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_GROUPED_SOV,
      ]
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      return [
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_FLAT_SOV,
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_GROUPED_SOV,
        ImportIntegrationComboJobMethod.MULTIPLE_PROJECTS,
      ]
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return []
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
      return [
        ImportIntegrationComboJobMethod.SINGLE_PROJECT_FLAT_SOV,
        ImportIntegrationComboJobMethod.MULTIPLE_PROJECTS_ONE_CONTRACT,
      ]
  }
}

/**
 * Generates a sequential invoice code based on a given set of invoice codes. We attempt to guess
 * the desired invoice code by taking the highest numeric invoice code and adding one.
 */
export function generateSequentialInvoiceCode(
  invoiceCodes: string[],
  integrationType:
    | IntegrationType.SAGE_100_CONTRACTOR
    | IntegrationType.SAGE_100_CONTRACTOR_AGAVE
    | IntegrationType.SAGE_100_CONTRACTOR_PEYOTE
    | IntegrationType.SAGE_300_CRE
    | IntegrationType.SAGE_300_CRE_AGAVE
    | IntegrationType.FOUNDATION
    | IntegrationType.CMIC
    | IntegrationType.CMIC_PEYOTE
): string | null {
  // Determine the invoice code by adding 1 to the highest code recorded so far. Only look at
  // invoices that are pure numbers. Not all companies are consistent and some mix in code-based
  // invoices in addition to manually entering specific names
  const invoicesWithCodes = invoiceCodes
    .filter((invoiceCode) => DIGITS_REGEX.test(invoiceCode))
    .map((invoiceCode) => parseInt(invoiceCode))
  const highestInvoiceCode = _.max(invoicesWithCodes) ?? 0
  const invoiceCode = (highestInvoiceCode + 1).toFixed(0)

  // If the next invoice code would be too long for the integration to accept, return null
  switch (integrationType) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE: {
      if (invoiceCode.length > MAX_SAGE_INVOICE_CODE_LENGTH) {
        return null
      }
      break
    }
    case IntegrationType.FOUNDATION: {
      if (invoiceCode.length > MAX_FOUNDATION_INVOICE_NUMBER_LENGTH) {
        return null
      }
      break
    }
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE: {
      if (invoiceCode.length > MAX_CMIC_INVOICE_NUMBER_LENGTH) {
        return null
      }
      break
    }
  }
  return invoiceCode
}

/**
 * Returns whether a sync payload is a pay app payload (a simple pay app sync)
 */
export function isPayAppPayload(
  type: integrationTypes.WriteSyncPayload['type']
): type is integrationTypes.WriteSyncPayloadPayApp['type'] {
  switch (type) {
    case 'payAppFoundation':
    case 'payAppSage100':
    case 'payAppTextura':
    case 'payAppGcPay':
    case 'payAppProcore':
    case 'payAppManual':
    case 'payAppCmic':
    case 'payAppQuickbooksOnline':
      return true
    case 'payAppLineItemsQuickbooksOnline':
    case 'payAppLineItemsAcumatica':
    case 'payAppLineItemsSage300':
    case 'payAppLineItemsSpectrum':
    case 'payAppLineItemsVista':
    case 'payAppLineItemsSageIntacct':
    case 'payAppFoundationFileGenie':
    case 'payAppFoundationFileFsi':
    case 'payAppQuickbooks':
    case 'payAppComputerEase':
    case 'lienWaivers':
    case 'legalRequirement':
    case 'payAppNetSuite':
    case 'changeOrderRequestVista':
      return false
  }
}

/**
 * Returns whether a sync payload is a pay app line items payload (a pay app sync with broken down line items)
 */
export function isPayAppLineItemsPayload(
  type: integrationTypes.WriteSyncPayload['type']
): type is integrationTypes.WriteSyncPayloadPayAppLineItems['type'] {
  switch (type) {
    case 'payAppLineItemsAcumatica':
    case 'payAppLineItemsSage300':
    case 'payAppLineItemsSpectrum':
    case 'payAppLineItemsVista':
    case 'payAppLineItemsSageIntacct':
    case 'payAppLineItemsQuickbooksOnline':
      return true
    case 'payAppQuickbooksOnline':
    case 'payAppFoundation':
    case 'payAppSage100':
    case 'payAppTextura':
    case 'payAppGcPay':
    case 'payAppProcore':
    case 'payAppQuickbooks':
    case 'payAppFoundationFileGenie':
    case 'payAppFoundationFileFsi':
    case 'payAppComputerEase':
    case 'payAppManual':
    case 'lienWaivers':
    case 'legalRequirement':
    case 'payAppCmic':
    case 'payAppNetSuite': // NOTE NetSuite does export files with line items, but they are not recorded in the payload
    case 'changeOrderRequestVista':
      return false
  }
}

/**
 * Returns the pay app ID of a write sync payload
 */
export function getPayAppIdFromWriteSyncPayload(
  payload: integrationTypes.WriteSyncPayload
): string | null {
  switch (payload.type) {
    case 'payAppTextura':
    case 'payAppGcPay':
    case 'payAppProcore':
    case 'payAppSage100':
    case 'payAppFoundation':
    case 'payAppQuickbooks':
    case 'payAppFoundationFileGenie':
    case 'payAppFoundationFileFsi':
    case 'payAppComputerEase':
    case 'payAppManual':
    case 'payAppLineItemsSage300':
    case 'payAppLineItemsSpectrum':
    case 'payAppLineItemsVista':
    case 'payAppLineItemsAcumatica':
    case 'payAppLineItemsSageIntacct':
    case 'payAppCmic':
    case 'payAppNetSuite':
    case 'payAppLineItemsQuickbooksOnline':
    case 'payAppQuickbooksOnline':
      return payload.payAppId
    case 'legalRequirement':
    case 'lienWaivers':
    case 'changeOrderRequestVista':
      return null
  }
}

type WriteSyncOperation = {
  status: WriteSyncOperationStatus
  result: Record<string, unknown> | null
}

/**
 * Returns whether a write sync operation was completed AND successful
 */
export function isWriteSyncSuccessful(sync: WriteSyncOperation): boolean {
  const result = sync.result as integrationTypes.WriteSyncResult | null
  return sync.status === WriteSyncOperationStatus.COMPLETED && result?.type === 'success'
}

/**
 * Returns whether a write sync operation was completed AND failed
 */
export function isWriteSyncFailed(sync: WriteSyncOperation): boolean {
  const result = sync.result as integrationTypes.WriteSyncResult | null
  return sync.status === WriteSyncOperationStatus.COMPLETED && result?.type === 'failure'
}

/**
 * Returns whether a write sync operation is in progress
 */
export function isWriteSyncInProgress(sync: WriteSyncOperation): boolean {
  const status = sync.status
  switch (status) {
    case WriteSyncOperationStatus.NOT_STARTED:
    case WriteSyncOperationStatus.RUNNING:
      return true
    case WriteSyncOperationStatus.CANCELED:
    case WriteSyncOperationStatus.COMPLETED:
    case WriteSyncOperationStatus.DEFERRED:
    case WriteSyncOperationStatus.QUEUED_IN_HH2:
      return false
  }
}

type PayAppWithLastErpSync = {
  createdAt: string
  readonly lastErpSyncs: readonly WriteSyncOperation[]
}

/**
 * In the UI, we ultimately need to know that the pay app ERP sync status is 1 of 3 statuses:
 * 1. The pay app is synced to the ERP (successfully/completed)
 * 2. The pay app is NOT synced to the ERP (failed/never synced)
 * 3. A sync is pending
 */
export function getPayAppLastErpSyncStatus(
  payApp: PayAppWithLastErpSync
): 'synced' | 'notSynced' | 'pending' {
  if (payApp.lastErpSyncs.length === 0) {
    return 'notSynced'
  }

  const somePending = payApp.lastErpSyncs.some(({ status }) => {
    switch (status) {
      case WriteSyncOperationStatus.NOT_STARTED:
      case WriteSyncOperationStatus.RUNNING:
      case WriteSyncOperationStatus.QUEUED_IN_HH2:
        return true
      case WriteSyncOperationStatus.CANCELED:
      case WriteSyncOperationStatus.COMPLETED:
      case WriteSyncOperationStatus.DEFERRED:
        return false
    }
  })
  if (somePending) {
    return 'pending'
  }

  const allSuccessful = payApp.lastErpSyncs.every(isWriteSyncSuccessful)
  if (allSuccessful) {
    return 'synced'
  }

  return 'notSynced'
}

/**
 * When exporting Quickbooks invoices, we generally give users the option to either include
 * retention as a separate lump sum line item on the invoice, or to deduct it from the progress line
 * items. If retention is tracked at the pay app or progress level though, or if there's a pay-app
 * level retention override, we wouldn't be able to fold it into individual line items, so we
 * require including a separate retention line item. We also require it when no retention is
 * tracked, since it doesn't make sense to show an option in that case either.
 */
export function doesQuickbooksContractRequireIncludeRetentionSeparately(
  hasRetentionOverride: boolean,
  retentionTrackingLevel: RetentionTrackingLevel
): boolean {
  return (
    hasRetentionOverride ||
    retentionTrackingLevel === RetentionTrackingLevel.PAY_APP ||
    retentionTrackingLevel === RetentionTrackingLevel.PROJECT ||
    retentionTrackingLevel === RetentionTrackingLevel.NONE
  )
}

// This should always be true because it supports all retention tracking levels. If it were ever
// switched to false, we'd need to check whether including retention is required for the contract
// and apply a default setting based on the retention level.
export const QUICKBOOKS_DEFAULT_INCLUDE_RETENTION = true
export const QUICKBOOKS_DEFAULT_COMBINE_AS_SINGLE_LINE = false
/**
 * Because Quickbooks doesn't have the concept of contracts or SOVs, we give the option to choose if
 * you want to use your SOV as is or if you want to sum everything up into one line item name. If
 * the latter, this is the name of that "item".
 */
export const QUICKBOOKS_DEFAULT_SINGLE_LINE_ITEM_DESCRIPTION = 'Siteline Billed'

/** Returns whether the integration is a file-based ERP, an api-based ERP, or neither */
export function isFileBasedIntegration(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function supportsReleasingRetention(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.TEXTURA:
      return true
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
  }
}

export function doesProjectsResponseIncludeGeneralContractor(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
    case IntegrationType.SAGE_300_CRE_AGAVE:
      // We currently do not include GCs in the projects response because customers are set on
      // line items, not contracts, and we don't include contract line item detail when requesting
      // all projects
      return false
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

export function supportsReadingPreviousBilledByLineItem(
  type: IntegrationType,
  changeOrdersMethod?: ImportIntegrationChangeOrdersMethod
): boolean {
  switch (type) {
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.NETSUITE_FILE:
      // CMiC only exposes a running billed-to-date total per line item, not per-invoice history,
      // so we can't read previous billed as of a reference date
      // We can't read previous billed by line items from Spectrum
      // We don't get history from file-based integrations
      return false

    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      // Note this is generally true, but there is a case where the project does not have a contract/proposal
      // and no progress billings, so we can only read change order data (which doesn't have previous billed)
      // In that case, the previous billed per line item amount will be zero
      return true

    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
      // We set previous billed to zero when we import change orders as separate line items
      // because the customer has not been billing against the broken out SOV
      return changeOrdersMethod === ImportIntegrationChangeOrdersMethod.MERGE_ORIGINAL_LINE_ITEMS

    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.TEXTURA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.GC_PAY:
    case IntegrationType.TEST:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return true
  }
}

export function supportsContractMismatchReport(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return true
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**
 * Whether the integration can report every mapped contract's total in a handful of bulk reads,
 * which is what the contract value mismatch report relies on to stay cheap. Sage Intacct reads
 * PROJECTCONTRACT headers 500 ids at a time; other integrations would need their own bulk read.
 */
export function supportsContractValueMismatchReport(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return true
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.NETSUITE_FILE:
      return false
  }
}

/**

 * Extracts the change order request IDs from a write sync payload, if any exist.
 * Returns null if the payload doesn't contain change order request IDs.
 */
export function getChangeOrderRequestIdsFromWriteSyncPayload(
  payload: integrationTypes.WriteSyncPayload
): string[] | null {
  switch (payload.type) {
    case 'changeOrderRequestVista':
      return payload.changeOrderRequestIds
    case 'payAppTextura':
    case 'payAppGcPay':
    case 'payAppProcore':
    case 'payAppSage100':
    case 'payAppFoundation':
    case 'payAppQuickbooks':
    case 'payAppFoundationFileGenie':
    case 'payAppFoundationFileFsi':
    case 'payAppComputerEase':
    case 'payAppManual':
    case 'payAppLineItemsSage300':
    case 'payAppLineItemsSpectrum':
    case 'payAppLineItemsVista':
    case 'payAppLineItemsAcumatica':
    case 'payAppLineItemsSageIntacct':
    case 'payAppCmic':
    case 'payAppNetSuite':
    case 'payAppLineItemsQuickbooksOnline':
    case 'payAppQuickbooksOnline':
    case 'legalRequirement':
    case 'lienWaivers':
      return null
  }
}

/**
 * Used to limit the concurrency of contract mismatch reports to one per company for
 * contracts with integrations that need strict rate limiting.
 */
export function integrationBottleneckedForContractMismatchReport(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return true
  }
}

/**
 * Can we import vendor commitments from this integration?
 */
export function supportsImportingVendorCommitments(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return false
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
    case IntegrationType.VISTA_PEYOTE:
    case IntegrationType.VISTA:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
      return true
  }
}

type IntegrationMigration = { fromType: IntegrationType; toType: IntegrationType }

/**
 * Returns the integration types that a given type can be migrated to. These are the only migrations
 * we've tested the migration flow with; this will likely work for other integrations, but we should
 * add them explicitly as needed.
 */
function getMigrationCandidates(type: IntegrationType): IntegrationType[] {
  switch (type) {
    case IntegrationType.SAGE_100_CONTRACTOR:
      return [IntegrationType.SAGE_100_CONTRACTOR_AGAVE, IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
      return [IntegrationType.SAGE_100_CONTRACTOR, IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      return [IntegrationType.SAGE_100_CONTRACTOR, IntegrationType.SAGE_100_CONTRACTOR_AGAVE]
    case IntegrationType.SAGE_300_CRE:
      return [
        IntegrationType.SAGE_300_CRE_AGAVE,
        IntegrationType.SAGE_INTACCT,
        IntegrationType.ACUMATICA,
      ]
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return [IntegrationType.SAGE_300_CRE, IntegrationType.ACUMATICA]
    case IntegrationType.SAGE_INTACCT:
      return [IntegrationType.SAGE_300_CRE, IntegrationType.SAGE_INTACCT_PEYOTE]
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return [IntegrationType.SAGE_300_CRE, IntegrationType.SAGE_INTACCT]
    case IntegrationType.PROCORE:
      return [IntegrationType.PROCORE_PEYOTE]
    case IntegrationType.PROCORE_PEYOTE:
      return [IntegrationType.PROCORE]
    case IntegrationType.QUICKBOOKS_ONLINE:
      return [IntegrationType.QUICKBOOKS_ONLINE_PEYOTE]
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return [IntegrationType.QUICKBOOKS_ONLINE]
    case IntegrationType.VISTA_PEYOTE:
      return [IntegrationType.VISTA]
    case IntegrationType.VISTA:
      return [IntegrationType.VISTA_PEYOTE]
    case IntegrationType.ACUMATICA_PEYOTE:
      return [IntegrationType.ACUMATICA]
    case IntegrationType.ACUMATICA:
      return [IntegrationType.ACUMATICA_PEYOTE]
    case IntegrationType.SPECTRUM:
      return [IntegrationType.SPECTRUM_PEYOTE]
    case IntegrationType.SPECTRUM_PEYOTE:
      return [IntegrationType.SPECTRUM]
    case IntegrationType.CMIC_PEYOTE:
      return [IntegrationType.CMIC]
    case IntegrationType.FOUNDATION:
      return [IntegrationType.FOUNDATION_PEYOTE]
    case IntegrationType.FOUNDATION_PEYOTE:
      return [IntegrationType.FOUNDATION]
    case IntegrationType.CMIC:
      return [IntegrationType.CMIC_PEYOTE]
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
      return []
  }
}

/**
 * Returns integration types that represent different API providers for the same underlying ERP
 * instance. Matching by source ID is only guaranteed between Agave and Peyote integrations
 * (see `isAgavePeyoteCompatibleMigration`); other same-instance pairs (e.g. Sage 100 HH2 → Agave)
 * may not share source IDs.
 */
function getSameInstanceCandidates(type: IntegrationType): IntegrationType[] {
  switch (type) {
    case IntegrationType.PROCORE:
      return [IntegrationType.PROCORE_PEYOTE]
    case IntegrationType.PROCORE_PEYOTE:
      return [IntegrationType.PROCORE]
    case IntegrationType.QUICKBOOKS_ONLINE:
      return [IntegrationType.QUICKBOOKS_ONLINE_PEYOTE]
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return [IntegrationType.QUICKBOOKS_ONLINE]
    case IntegrationType.SAGE_100_CONTRACTOR:
      return [IntegrationType.SAGE_100_CONTRACTOR_AGAVE, IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
      return [IntegrationType.SAGE_100_CONTRACTOR, IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      return [IntegrationType.SAGE_100_CONTRACTOR, IntegrationType.SAGE_100_CONTRACTOR_AGAVE]
    case IntegrationType.SAGE_300_CRE:
      return [IntegrationType.SAGE_300_CRE_AGAVE]
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return [IntegrationType.SAGE_300_CRE]
    case IntegrationType.FOUNDATION_PEYOTE:
      return [IntegrationType.FOUNDATION, IntegrationType.FOUNDATION_FILE]
    case IntegrationType.FOUNDATION:
      return [IntegrationType.FOUNDATION_FILE, IntegrationType.FOUNDATION_PEYOTE]
    case IntegrationType.FOUNDATION_FILE:
      return [IntegrationType.FOUNDATION, IntegrationType.FOUNDATION_PEYOTE]
    case IntegrationType.VISTA_PEYOTE:
      return [IntegrationType.VISTA]
    case IntegrationType.VISTA:
      return [IntegrationType.VISTA_PEYOTE]
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return [IntegrationType.SAGE_INTACCT]
    case IntegrationType.SAGE_INTACCT:
      return [IntegrationType.SAGE_INTACCT_PEYOTE]
    case IntegrationType.ACUMATICA_PEYOTE:
      return [IntegrationType.ACUMATICA]
    case IntegrationType.ACUMATICA:
      return [IntegrationType.ACUMATICA_PEYOTE]
    case IntegrationType.SPECTRUM:
      return [IntegrationType.SPECTRUM_PEYOTE]
    case IntegrationType.SPECTRUM_PEYOTE:
      return [IntegrationType.SPECTRUM]
    case IntegrationType.CMIC_PEYOTE:
      return [IntegrationType.CMIC]
    case IntegrationType.CMIC:
      return [IntegrationType.CMIC_PEYOTE]
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
      return []
  }
}

/**
 * Returns true if the migration is between two integrations that can represent the same
 * ERP instance (same type or different API providers for the same system).
 */
export function isPotentialSameInstanceMigration(migration: IntegrationMigration): boolean {
  if (migration.fromType === migration.toType) {
    return true
  }
  return getSameInstanceCandidates(migration.fromType).includes(migration.toType)
}

/**
 * Returns the Peyote counterpart for an Agave integration, or the Agave counterpart for a Peyote
 * integration. Peyote is a Siteline reimplementation of the Agave API that returns the same shape
 * and source IDs, so both are interchangeable.
 */
function getAgavePeyoteCounterpart(type: IntegrationType): IntegrationType | null {
  switch (type) {
    case IntegrationType.PROCORE:
      return IntegrationType.PROCORE_PEYOTE
    case IntegrationType.PROCORE_PEYOTE:
      return IntegrationType.PROCORE
    case IntegrationType.QUICKBOOKS_ONLINE:
      return IntegrationType.QUICKBOOKS_ONLINE_PEYOTE
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return IntegrationType.QUICKBOOKS_ONLINE
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
      return IntegrationType.SAGE_100_CONTRACTOR_PEYOTE
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
      return IntegrationType.SAGE_100_CONTRACTOR_AGAVE
    case IntegrationType.VISTA_PEYOTE:
      return IntegrationType.VISTA
    case IntegrationType.VISTA:
      return IntegrationType.VISTA_PEYOTE
    case IntegrationType.FOUNDATION_PEYOTE:
      return IntegrationType.FOUNDATION
    case IntegrationType.FOUNDATION:
      return IntegrationType.FOUNDATION_PEYOTE
    case IntegrationType.SAGE_INTACCT_PEYOTE:
      return IntegrationType.SAGE_INTACCT
    case IntegrationType.SAGE_INTACCT:
      return IntegrationType.SAGE_INTACCT_PEYOTE
    case IntegrationType.ACUMATICA_PEYOTE:
      return IntegrationType.ACUMATICA
    case IntegrationType.ACUMATICA:
      return IntegrationType.ACUMATICA_PEYOTE
    case IntegrationType.SPECTRUM:
      return IntegrationType.SPECTRUM_PEYOTE
    case IntegrationType.SPECTRUM_PEYOTE:
      return IntegrationType.SPECTRUM
    case IntegrationType.CMIC_PEYOTE:
      return IntegrationType.CMIC
    case IntegrationType.CMIC:
      return IntegrationType.CMIC_PEYOTE
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.NETSUITE_FILE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
      return null
  }
}

/**
 * Returns true if the migration is between an Agave and Peyote integration for the same source
 * system. These are interchangeable because Peyote reimplements the Agave API with the same
 * shape and source IDs.
 */
export function isAgavePeyoteCompatibleMigration(migration: IntegrationMigration): boolean {
  return getAgavePeyoteCounterpart(migration.fromType) === migration.toType
}

export function isSupportedIntegrationMigration(migration: IntegrationMigration): boolean {
  if (migration.fromType === migration.toType) {
    // We always support migrating within the same integration type, e.g. if a customer
    // switches from an on-premise to hosted version of their ERP
    return true
  }
  return getMigrationCandidates(migration.fromType).includes(migration.toType)
}
